import axios from 'axios';
import { baseUrl } from '../config/config';
import { emailService } from './emailService';

// Notification Types Enum
export const NotificationTypes = {
    // User Account Notifications
    ACCOUNT_CREATED: 'ACCOUNT_CREATED',
    PASSWORD_CHANGED: 'PASSWORD_CHANGED',
    PROFILE_UPDATED: 'PROFILE_UPDATED',
    SECURITY_ALERT: 'SECURITY_ALERT',
    EMAIL_VERIFICATION: 'EMAIL_VERIFICATION',
    
    // Assessment/Submission Notifications
    SUBMISSION_DEADLINE: 'SUBMISSION_DEADLINE',
    SUBMISSION_COMPLETED: 'SUBMISSION_COMPLETED',
    ASSESSMENT_PROGRESS: 'ASSESSMENT_PROGRESS',
    MISSING_INFO: 'MISSING_INFO',
    SUBMISSION_STATUS: 'SUBMISSION_STATUS',
    REPORT_GENERATED: 'REPORT_GENERATED',
    
    // System Alerts
    SYSTEM_MAINTENANCE: 'SYSTEM_MAINTENANCE',
    SERVICE_UPDATE: 'SERVICE_UPDATE',
    TECHNICAL_ISSUE: 'TECHNICAL_ISSUE',
    NEW_FEATURE: 'NEW_FEATURE',
    PERFORMANCE_ALERT: 'PERFORMANCE_ALERT',
    
    // Report Notifications
    REPORT_REVIEW: 'REPORT_REVIEW',
    REPORT_APPROVED: 'REPORT_APPROVED',
    REPORT_SHARED: 'REPORT_SHARED',
    REPORT_UPDATED: 'REPORT_UPDATED',
    
    // Compliance and Policy Notifications
    POLICY_UPDATE: 'POLICY_UPDATE',
    COMPLIANCE_DEADLINE: 'COMPLIANCE_DEADLINE',
    DATA_SUBMISSION: 'DATA_SUBMISSION',
    REGULATORY_UPDATE: 'REGULATORY_UPDATE',
    PRIVACY_UPDATE: 'PRIVACY_UPDATE',
    
    // Support and Communication
    SUPPORT_TICKET: 'SUPPORT_TICKET',
    QUERY_RESPONSE: 'QUERY_RESPONSE',
    FEEDBACK_REQUEST: 'FEEDBACK_REQUEST',
    SURVEY_INVITATION: 'SURVEY_INVITATION',
    NEWSLETTER: 'NEWSLETTER'
};

// Notification Priority Levels
export const NotificationPriority = {
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
    HIGH: 'HIGH',
    URGENT: 'URGENT'
};

// Notification Delivery Channels
export const NotificationChannels = {
    EMAIL: 'EMAIL',
    IN_APP: 'IN_APP',
    BOTH: 'BOTH'
};

class NotificationService {
    constructor() {
        this.notifications = new Map();
        this.userPreferences = new Map();
    }

    async sendNotification({ type, userId, data, priority = NotificationPriority.MEDIUM, channels = [NotificationChannels.BOTH] }) {
        try {
            // Get user preferences
            const preferences = await this.getUserPreferences(userId);
            
            // Check if user wants to receive this type of notification
            if (!preferences[type]?.enabled) {
                return;
            }

            const notification = {
                id: Date.now().toString(),
                type,
                userId,
                data,
                priority,
                channels,
                timestamp: new Date(),
                read: false
            };

            // Store notification
            if (!this.notifications.has(userId)) {
                this.notifications.set(userId, []);
            }
            this.notifications.get(userId).push(notification);

            // Send through appropriate channels
            if (channels.includes(NotificationChannels.EMAIL) || channels.includes(NotificationChannels.BOTH)) {
                await emailService.sendTemplateEmail({
                    to: data.email,
                    template: type,
                    data: {
                        ...data,
                        priority
                    }
                });
            }

            return notification;
        } catch (error) {
            console.error('Error sending notification:', error);
            throw error;
        }
    }

    async getUserPreferences(userId) {
        if (!this.userPreferences.has(userId)) {
            // Default preferences
            this.userPreferences.set(userId, {
                [NotificationTypes.ACCOUNT_CREATED]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.PASSWORD_CHANGED]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.PROFILE_UPDATED]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.SECURITY_ALERT]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.EMAIL_VERIFICATION]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.SUBMISSION_DEADLINE]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.SUBMISSION_COMPLETED]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.ASSESSMENT_PROGRESS]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.MISSING_INFO]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.SUBMISSION_STATUS]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.REPORT_GENERATED]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.SYSTEM_MAINTENANCE]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.SERVICE_UPDATE]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.TECHNICAL_ISSUE]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.NEW_FEATURE]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.PERFORMANCE_ALERT]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.REPORT_REVIEW]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.REPORT_APPROVED]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.REPORT_SHARED]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.REPORT_UPDATED]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.POLICY_UPDATE]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.COMPLIANCE_DEADLINE]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.DATA_SUBMISSION]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.REGULATORY_UPDATE]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.PRIVACY_UPDATE]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.SUPPORT_TICKET]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.QUERY_RESPONSE]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.FEEDBACK_REQUEST]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.SURVEY_INVITATION]: { enabled: true, email: true, inApp: true },
                [NotificationTypes.NEWSLETTER]: { enabled: true, email: true, inApp: true }
            });
        }
        return this.userPreferences.get(userId);
    }

    async updateUserPreferences(userId, preferences) {
        this.userPreferences.set(userId, preferences);
        // In a real application, you would persist these preferences to a database
    }

    async getUserNotifications(userId) {
        return this.notifications.get(userId) || [];
    }

    async markNotificationAsRead(userId, notificationId) {
        const userNotifications = this.notifications.get(userId);
        if (userNotifications) {
            const notification = userNotifications.find(n => n.id === notificationId);
            if (notification) {
                notification.read = true;
            }
        }
    }

    async deleteNotification(userId, notificationId) {
        const userNotifications = this.notifications.get(userId);
        if (userNotifications) {
            const index = userNotifications.findIndex(n => n.id === notificationId);
            if (index !== -1) {
                userNotifications.splice(index, 1);
            }
        }
    }

    async markAllNotificationsAsRead(userId) {
        const userNotifications = this.notifications.get(userId);
        if (userNotifications) {
            userNotifications.forEach(notification => {
                notification.read = true;
            });
        }
    }

    async deleteAllNotifications(userId) {
        this.notifications.delete(userId);
    }

    getUnreadNotificationCount(userId) {
        const userNotifications = this.notifications.get(userId);
        return userNotifications ? userNotifications.filter(n => !n.read).length : 0;
    }
}

export const notificationService = new NotificationService();

// Helper function to format notification data
export const formatNotificationData = (type, data, language = 'English') => {
    const templates = {
        [NotificationTypes.ACCOUNT_CREATED]: {
            title: language === 'English' ? 'Account Created Successfully' : 'Compte créé avec succès',
            message: language === 'English' 
                ? `Welcome to our platform! Your account has been created successfully.`
                : `Bienvenue sur notre plateforme ! Votre compte a été créé avec succès.`
        },
        [NotificationTypes.PASSWORD_CHANGED]: {
            title: language === 'English' ? 'Password Changed' : 'Mot de passe modifié',
            message: language === 'English'
                ? 'Your password has been changed successfully.'
                : 'Votre mot de passe a été modifié avec succès.'
        },
        // Add more templates for other notification types
    };

    return {
        ...templates[type],
        data,
        timestamp: new Date().toISOString()
    };
}; 